import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { TableInput } from "components/common/input";
import type { INoticeList } from "service/interface/notice";
import moment from "moment";
import { Editor } from "@toast-ui/react-editor";
import "@toast-ui/editor/dist/i18n/ko-kr";
import colorSyntax from "@toast-ui/editor-plugin-color-syntax";
import "tui-color-picker/dist/tui-color-picker.css";
import "@toast-ui/editor-plugin-color-syntax/dist/toastui-editor-plugin-color-syntax.css";
import "@toast-ui/editor/dist/toastui-editor.css";

interface Props {
  data: INoticeList;
  setData: React.Dispatch<React.SetStateAction<INoticeList>>;
}

export function NoticeInfoTable({ data, setData }: Props) {
  const params = useParams();
  const editorRef = useRef<Editor>(null);
  const [isEditorUpdate, setIsEditorUpdate] = useState<boolean>(false);

  useEffect(() => {
    if (data?.content && !isEditorUpdate) {
      editorRef.current?.getInstance().setMarkdown(data.content);
      setIsEditorUpdate(true);
    }
  }, [data]);

  const handleTitleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({
      ...prev,
      title: e.target.value,
    }));
  };

  const handleContentEditor = () => {
    const data = editorRef.current?.getInstance().getMarkdown();
    setData((prev) => ({
      ...prev,
      content: data!,
    }));
  };

  const handleLinkInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({
      ...prev,
      link: e.target.value,
    }));
  };

  return (
    <div>
      <div className="mb-[24px] flex items-center justify-between">
        <p className="m-0 text-[24px] font-semibold leading-[30px] text-black">
          게시글 정보
        </p>
      </div>
      <div className="w-full border-t-[2px] border-t-black p-0">
        <div className="flex h-[86px] w-full border-b-[1px] border-b-gray-1">
          <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            제목
          </div>
          <div className="flex h-full w-[787px] items-center px-[12px] text-M3 text-gray-5">
            <TableInput
              value={data.title}
              onChange={handleTitleInput}
              placeholder="제목을 입력해 주세요"
            />
          </div>
        </div>
        {params.id !== "add" && (
          <div className="flex h-[58px] w-full border-b-[1px] border-b-gray-1">
            <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
              생성일
            </div>
            <div className="flex h-full w-[787px] items-center px-[24px] text-M3 text-gray-5">
              {moment(data.createdAt).format("YYYY-MM-DD")}
            </div>
          </div>
        )}
        <div className="flex h-[600px] w-full border-b-[1px] border-b-gray-1">
          <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            내용
          </div>
          <div className="flex h-full !w-[787px] items-center px-[12px] text-M3 text-gray-5">
            <div className="w-full">
              <Editor
                previewStyle="vertical"
                height="576px"
                initialEditType="wysiwyg"
                useCommandShortcut={true}
                hideModeSwitch
                plugins={[colorSyntax]}
                language="ko-KR"
                ref={editorRef}
                onChange={handleContentEditor}
              />
            </div>
          </div>
        </div>
        <div className="flex h-[86px] w-full border-b-[1px] border-b-gray-1">
          <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            하이퍼링크
          </div>
          <div className="flex h-full w-[787px] items-center px-[12px] text-M3 text-gray-5">
            <TableInput
              value={data.link}
              onChange={handleLinkInput}
              placeholder="링크를 입력해 주세요 ex) http://"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
